// utils/fbPixel.ts — Meta Pixel helpers
// The Pixel script itself is injected on-demand by initFbPixel().

const PIXEL_ID = '1215304437119773'

type FbqData = Record<string, any>

function getFbq(): any | null {
  if (typeof window === 'undefined') return null
  return (window as any).fbq || null
}

/**
 * Inject the Meta Pixel script (once) and fire PageView.
 * Safe to call multiple times.
 */
export function initFbPixel() {
  if (typeof window === 'undefined') return
  const w = window as any
  if (w.fbq) return // already initialized

  // Standard Meta Pixel boot sequence
  const n: any = (w.fbq = function () {
    n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
  })
  if (!w._fbq) w._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []

  w.fbq('init', PIXEL_ID)
  w.fbq('track', 'PageView')

  const t = document.createElement('script')
  t.async = true
  t.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const s = document.getElementsByTagName('script')[0]
  s?.parentNode?.insertBefore(t, s)
}

/**
 * Track a standard Meta Pixel event (e.g. Lead, ViewContent, InitiateCheckout).
 */
export function trackFbEvent(event: string, data?: FbqData) {
  const fbq = getFbq()
  if (typeof fbq === 'function') fbq('track', event, data)
}

/**
 * Track a custom (non-standard) Meta Pixel event.
 */
export function trackFbCustomEvent(event: string, data?: FbqData) {
  const fbq = getFbq()
  if (typeof fbq === 'function') fbq('trackCustom', event, data)
}

/**
 * Observe a map of CSS selectors -> section names.
 * Fires a custom `SectionView` event the first time each section becomes ≥30% visible.
 * Returns a cleanup function.
 */
export function trackSectionViews(
  selectorMap: Record<string, string>,
): () => void {
  if (
    typeof window === 'undefined' ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return () => {}
  }

  const fired = new Set<string>()
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        const name = (entry.target as HTMLElement).dataset.sectionName
        if (!name || fired.has(name)) continue
        fired.add(name)
        trackFbCustomEvent('SectionView', { section_name: name })
        observer.unobserve(entry.target)
      }
    },
    { threshold: 0.3 },
  )

  // Wait a tick so React has rendered the targets
  const timer = setTimeout(() => {
    Object.entries(selectorMap).forEach(([selector, name]) => {
      const el = document.querySelector(selector) as HTMLElement | null
      if (!el) return
      el.dataset.sectionName = name
      observer.observe(el)
    })
  }, 0)

  return () => {
    clearTimeout(timer)
    observer.disconnect()
  }
}

/**
 * Fire custom events at 25%, 50%, 75% and 100% scroll depth.
 * Returns a cleanup function.
 */
export function trackScrollDepth(): () => void {
  if (typeof window === 'undefined') return () => {}

  const milestones = [25, 50, 75, 100]
  const fired = new Set<number>()
  let ticking = false

  const handler = () => {
    if (ticking) return
    ticking = true
    requestAnimationFrame(() => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight
      const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0
      for (const m of milestones) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m)
          trackFbCustomEvent('ScrollDepth', { percent: m })
        }
      }
      ticking = false
    })
  }

  window.addEventListener('scroll', handler, { passive: true })
  return () => window.removeEventListener('scroll', handler)
}

/**
 * Fire custom events at 30s, 60s, 120s and 300s on page.
 * Returns a cleanup function.
 */
export function trackTimeOnPage(): () => void {
  if (typeof window === 'undefined') return () => {}

  const milestones = [30, 60, 120, 300]
  const timers = milestones.map((seconds) =>
    setTimeout(() => {
      trackFbCustomEvent('TimeOnPage', { seconds })
    }, seconds * 1000),
  )

  return () => timers.forEach(clearTimeout)
}
